import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import Rheostat from 'rheostat';
import 'rheostat/css/rheostat.css';
import 'rheostat/initialize';
import throttle from 'lodash.throttle';
import RouteContext from '../../../../../../routes/context';
import { trackProductCollectionFilterUpdate } from '../../../../tracking';
import {
  ProductCollectionRangeFilter,
  RangeSection as RangeSectionT,
} from '../../../../../../types';
import * as SectionStyles from '../styles';
import { getAlgorithmByScale } from './algorithms';
import SliderInput, { clamp } from './input';
import SliderPit from './pit';
import * as S from './styles';
import { getHistogramUtils, getHistogramScale } from './histogram';

type PitComponentProps = {
  style: object;
  children: number;
};

// Rheostat custom components
type RheostatProps = {
  handleRef: any;
};
const SliderProgressBar = ({ handleRef, ...passProps }: RheostatProps) => (
  <S.SliderProgressBar ref={handleRef} {...passProps} />
);
const SliderBackground = ({ handleRef, ...passProps }: RheostatProps) => (
  <S.SliderBackground ref={handleRef} {...passProps} />
);
const SliderHandle = ({ handleRef, ...passProps }: RheostatProps) => (
  <S.SliderHandle ref={handleRef} {...passProps} />
);

// Find current slider settings if present in filter
const getValuesFromState = (
  property: string,
  maxValues: number[],
  state: { filters?: ProductCollectionRangeFilter[] }
) => {
  const filters = (state && state.filters) || [];
  const current = filters.find(filter => filter.property === property) || {
    from: null,
    to: null,
  };
  return [current.from || maxValues[0], current.to || maxValues[1]].map(value =>
    clamp(value, maxValues[0], maxValues[1])
  );
};

// Throttle onChange for text inputs
const INPUT_THROTTLE = 500;
const throttledOnChange = throttle(
  (event, onChange) => onChange(event),
  INPUT_THROTTLE,
  { leading: false, trailing: true }
);

type Props = RangeSectionT & {
  showTitle?: boolean;
};

const RangeSection = ({
  id,
  title,
  property,
  min,
  max,
  count,
  unit,
  histogram,
  showTitle,
}: Props) => {
  const {
    getBucket,
    pitPoints,
    largestBucketCount,
    histogramStart,
    histogramEnd,
  } = useMemo(() => getHistogramUtils(histogram || undefined), [histogram]);
  const maxInterval = histogram
    ? [histogramStart, histogramEnd]
    : [min || 0, max || 0];
  const algorithm = useMemo(
    () => getAlgorithmByScale(getHistogramScale(histogram || undefined)),
    [histogram]
  );

  /**
   * MIN_SLIDER_UNIT = 1 can cause usability issues with overlapping handles.
   * Another option is to set it to some percentage of total interval, i.e.:
   * MIN_SLIDER_UNIT = Math.max(1, Math.round((maxInterval[1] - maxInterval[0]) * 0.05));
   * But this annoys the user.
   */
  const MIN_SLIDER_UNIT = 1;

  const disabled = count < 2 || maxInterval[0] === maxInterval[1];
  const [editField, setEditField] = useState(-1);
  const { dispatch, getState } = useContext(RouteContext);
  const values = getValuesFromState(property, maxInterval, getState());
  // Called while dragging
  const onValuesUpdated = useCallback(
    ({ values: [from, to] }: { values: number[] }) => {
      if (from === maxInterval[0] && to === maxInterval[1]) {
        dispatch({
          type: 'FILTER_REMOVE',
          property,
        });
      } else if (
        to >= maxInterval[0] + MIN_SLIDER_UNIT &&
        from <= maxInterval[1] - MIN_SLIDER_UNIT
      ) {
        dispatch({
          type: 'FILTER_RANGE_UPDATE',
          filter: {
            type: 'range',
            property,
            from,
            to,
          },
        });
      }
    },
    [dispatch, property, maxInterval]
  );
  // Called when values are commited (mouseup, click, etc)
  const onChange = useCallback(
    (event: { values: number[] }) => {
      const [from, to] = event.values;
      trackProductCollectionFilterUpdate(
        getState().filters,
        property,
        `${from}:${to}`
      );
      onValuesUpdated({ values: event.values });
    },
    [getState, onValuesUpdated, property]
  );
  const onFromChange = useCallback(
    (value: number) => {
      if (value !== values[0] && !isNaN(value)) {
        throttledOnChange({ values: [value, values[1]] }, onChange);
      }
    },
    [onChange, values]
  );
  const onToChange = useCallback(
    (value: number) => {
      if (value !== values[1] && !isNaN(value)) {
        throttledOnChange({ values: [values[0], value] }, onChange);
      }
    },
    [onChange, values]
  );
  const onFromClose = useCallback(
    (value: number) => {
      onFromChange(value);
      setEditField(-1);
    },
    [onFromChange]
  );
  const onToClose = useCallback(
    (value: number) => {
      onToChange(value);
      setEditField(-1);
    },
    [onToChange]
  );
  const startEditFrom = useCallback(() => setEditField(0), [setEditField]);
  const startEditTo = useCallback(() => setEditField(1), [setEditField]);
  const refs: React.MutableRefObject<HTMLElement | null>[] = [
    useRef(null),
    useRef(null),
  ];

  const pitComponent = useCallback(
    (props: PitComponentProps) => (
      <SliderPit
        getBucket={getBucket}
        largestBucketCount={largestBucketCount}
        selectedValues={values}
        {...props}
      />
    ),
    [getBucket, largestBucketCount, values]
  );

  return (
    <SectionStyles.Section key={id}>
      {showTitle && (
        <SectionStyles.SectionTitle>{title}</SectionStyles.SectionTitle>
      )}
      <S.SliderWrapper>
        <S.SliderRow flex>
          {editField === 0 ? (
            <SliderInput
              defaultValue={values[0].toString()}
              onChange={onFromChange}
              onClose={onFromClose}
              min={maxInterval[0]}
              max={Math.min(maxInterval[1], values[1]) - MIN_SLIDER_UNIT}
              handleRef={refs[0]}
            />
          ) : (
            <S.SliderInputValue
              onClick={startEditFrom}
              disabled={disabled}
              data-test="SliderInputValueFrom"
            >
              {values[0]}
            </S.SliderInputValue>
          )}
          {editField === 1 ? (
            <SliderInput
              defaultValue={values[1].toString()}
              onChange={onToChange}
              onClose={onToClose}
              min={Math.max(maxInterval[0], values[0]) + MIN_SLIDER_UNIT}
              max={maxInterval[1]}
              handleRef={refs[1]}
            />
          ) : (
            <S.SliderInputValue
              onClick={startEditTo}
              disabled={disabled}
              data-test="SliderInputValueTo"
            >
              {values[1]}
            </S.SliderInputValue>
          )}
        </S.SliderRow>
        <S.SliderContainer>
          <Rheostat
            algorithm={algorithm}
            disabled={disabled}
            values={values}
            min={maxInterval[0]}
            max={maxInterval[1]}
            pitPoints={pitPoints}
            pitComponent={pitComponent}
            handle={SliderHandle}
            background={SliderBackground}
            progressBar={SliderProgressBar}
            onChange={onChange}
            onValuesUpdated={onValuesUpdated}
          />
        </S.SliderContainer>
        <S.SliderRow flex>
          <S.SliderTitle>{`${min}${unit ? ` ${unit}` : ''}`}</S.SliderTitle>
          <S.SliderTitle>{`${max}${unit ? ` ${unit}` : ''}`}</S.SliderTitle>
        </S.SliderRow>
      </S.SliderWrapper>
    </SectionStyles.Section>
  );
};

export default RangeSection;
